import {
  type BufferGeometry,
  type Color,
  Group,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  MeshPhysicalMaterial,
  type Object3D,
  Quaternion,
  Vector3,
  type Material,
} from 'three';
import {
  hingeDegFromT,
  type HingeDef,
} from '@/render/hinges';
import type { ExportMetadata } from './metadata';

/**
 * USDA (Universal Scene Description, ASCII) exporter.
 *
 * The exporter walks the live scene graph and emits a self-contained
 * `.usda` file that can be opened in usdview, Houdini, Blender (via the
 * USD plugin), or Pixar's standalone Hydra renderer. Three.js ships no
 * built-in USD exporter so this module is hand-rolled.
 *
 * Hierarchy goals (reworked 2026-04):
 *   - Every named node in the scene graph becomes an Xform with its
 *     SEMANTIC name. Duplicate sibling names are disambiguated by a
 *     numeric suffix ("_001"), so no generic "pillar" prim ever shadows
 *     its neighbours.
 *   - Unnamed / generated-name meshes fall back to a generated label
 *     based on their parent (e.g. a loose trim bar under "Frame"
 *     becomes "Frame_Bar_001").
 *   - The tree mirrors the builders' intent:
 *         Structure (root)
 *           Lot
 *             BasePlate, Fence, Mailbox
 *           House
 *             Plinth, FrontSteps
 *             Story_0
 *               FloorSlab
 *               ExteriorWalls
 *                 Wall_front
 *                   Pillar_000, Sill_001, ..., Window_<id> { Frame, Sash, Pane }, Door_<id> { Frame, Leaf, Knob }
 *               InteriorWalls
 *               Stairs_to_L1
 *               Balcony_L0 (if any)
 *
 * Metadata (seed, preset, settings, timestamp, address, description)
 * is stored as `customLayerData` at the root of the file so USD
 * composition + DCC tool inspectors pick it up automatically, and as
 * `custom` attributes on the top-level Structure prim for redundancy.
 *
 * Materials are emitted inline as `primvars:displayColor` so usdview
 * renders colored geometry without any external material library.
 *
 * Hinges are encoded two ways:
 *   1. A custom property block on the pivot xform with all the
 *      metadata a downstream pipeline needs to rebuild the joint.
 *   2. A rotation op on the pivot whose authored value reflects the
 *      CURRENT user slider state, so the exported scene shows doors /
 *      windows in their open positions.
 */

export interface ExportUsdaOptions {
  /** When false (default), forces every descendant visible during export
   *  and restores afterward — same convention as the GLB exporter. */
  includeSliced?: boolean;
  /** Optional override for the live hinge values (id → 0..1). */
  hingeValues?: Record<string, number>;
  /** Per-id swing inversion flags. Applied to both the baked transform
   *  and the exported `hinge:inverted` custom attribute. */
  hingeInverted?: Record<string, boolean>;
  /** Display-friendly title for the file header comment. */
  title?: string;
  /** Metadata bundle (seed, preset, settings, timestamp, address ...). */
  metadata?: ExportMetadata;
}

export function exportToUsda(root: Object3D, opts: ExportUsdaOptions = {}): Blob {
  const text = buildUsdaText(root, opts);
  return new Blob([text], { type: 'model/vnd.usda+plain' });
}

export function buildUsdaText(root: Object3D, opts: ExportUsdaOptions = {}): string {
  const { includeSliced = false, title, metadata } = opts;

  // Force every node visible for the duration of the export so the file
  // contains the canonical scene, not the peeled view.
  const visibilitySnapshot = new Map<Object3D, boolean>();
  if (!includeSliced) {
    root.traverse((obj) => {
      visibilitySnapshot.set(obj, obj.visible);
      obj.visible = true;
    });
  }

  try {
    // Apply any caller-supplied hinge values to the scene transforms for
    // the duration of the export.
    const hingeSnapshot = new Map<Object3D, { rx: number; ry: number; rz: number }>();
    if (opts.hingeValues) {
      root.traverse((n) => {
        if (!(n instanceof Group) || !n.name.startsWith('Hinge_')) return;
        const def = n.userData?.hinge as HingeDef | undefined;
        if (!def) return;
        const id = def.id;
        if (!(id in opts.hingeValues!)) return;
        hingeSnapshot.set(n, { rx: n.rotation.x, ry: n.rotation.y, rz: n.rotation.z });
        const sign = opts.hingeInverted?.[id] ? -1 : 1;
        const rad = (sign * hingeDegFromT(def, opts.hingeValues![id]!) * Math.PI) / 180;
        n.rotation.set(0, 0, 0);
        if (def.axis === 'X') n.rotation.x = rad;
        if (def.axis === 'Y') n.rotation.y = rad;
        if (def.axis === 'Z') n.rotation.z = rad;
      });
    }

    try {
      ctx.inverted = opts.hingeInverted ?? {};
      ctx.names.clear();

      const lines: string[] = [];
      writeHeader(lines, title, metadata);

      // Wrap the entire export in a single defaultPrim Xform. Name is
      // fixed — "Structure" — so composition references are stable.
      lines.push(`def Xform "Structure" (`);
      lines.push(`    kind = "component"`);
      lines.push(`)`);
      lines.push(`{`);
      writeTransform(lines, '    ', new Matrix4());
      if (metadata) writeMetadataAttributes(lines, '    ', metadata);
      for (const child of root.children) {
        writePrim(lines, child, '    ');
      }
      lines.push(`}`);
      return lines.join('\n');
    } finally {
      ctx.inverted = {};
      ctx.names.clear();
      for (const [n, r] of hingeSnapshot) {
        if (n instanceof Group) n.rotation.set(r.rx, r.ry, r.rz);
      }
    }
  } finally {
    if (!includeSliced) {
      for (const [obj, vis] of visibilitySnapshot) {
        obj.visible = vis;
      }
    }
  }
}

/* -------------------- header ----------------------------------------- */

function writeHeader(lines: string[], title: string | undefined, metadata: ExportMetadata | undefined): void {
  lines.push(`#usda 1.0`);
  lines.push(`(`);
  lines.push(`    defaultPrim = "Structure"`);
  lines.push(`    metersPerUnit = 1`);
  lines.push(`    upAxis = "Y"`);
  lines.push(`    doc = "${escapeString(title ?? 'Structure house generator export')}"`);
  if (metadata) {
    lines.push(`    customLayerData = {`);
    lines.push(`        string generator = "${escapeString(metadata.generator)}"`);
    lines.push(`        string generatedAt = "${escapeString(metadata.generatedAt)}"`);
    lines.push(`        string seed = "${escapeString(metadata.seed)}"`);
    lines.push(`        string preset = "${escapeString(metadata.preset)}"`);
    lines.push(`        float score = ${metadata.score}`);
    lines.push(`        int schemaVersion = ${metadata.schemaVersion}`);
    if (metadata.siteAddress) {
      lines.push(`        string siteAddress = "${escapeString(metadata.siteAddress)}"`);
    }
    if (metadata.siteDescription) {
      lines.push(`        string siteDescription = "${escapeString(metadata.siteDescription)}"`);
    }
    lines.push(`        string settings = "${escapeString(JSON.stringify(metadata.settings))}"`);
    lines.push(`        string summary = "${escapeString(JSON.stringify(metadata.summary))}"`);
    lines.push(`    }`);
  }
  lines.push(`)`);
  lines.push(``);
}

function writeMetadataAttributes(lines: string[], indent: string, metadata: ExportMetadata): void {
  lines.push(`${indent}# --- Generation metadata -------------------------------------`);
  lines.push(`${indent}custom string meta:generator = "${escapeString(metadata.generator)}"`);
  lines.push(`${indent}custom string meta:generatedAt = "${escapeString(metadata.generatedAt)}"`);
  lines.push(`${indent}custom string meta:seed = "${escapeString(metadata.seed)}"`);
  lines.push(`${indent}custom string meta:preset = "${escapeString(metadata.preset)}"`);
  lines.push(`${indent}custom float meta:score = ${metadata.score}`);
  lines.push(`${indent}custom int meta:schemaVersion = ${metadata.schemaVersion}`);
  if (metadata.siteAddress) {
    lines.push(`${indent}custom string meta:siteAddress = "${escapeString(metadata.siteAddress)}"`);
  }
  if (metadata.siteDescription) {
    lines.push(`${indent}custom string meta:siteDescription = "${escapeString(metadata.siteDescription)}"`);
  }
  lines.push(`${indent}custom string meta:settings = "${escapeString(JSON.stringify(metadata.settings))}"`);
  lines.push(`${indent}custom string meta:summary = "${escapeString(JSON.stringify(metadata.summary))}"`);
  lines.push(`${indent}# ------------------------------------------------------------`);
}

/* -------------------- prim emission ---------------------------------- */

interface ExportContext {
  inverted: Record<string, boolean>;
  /** parent-relative name dedupe: key = parent prim path, value = Map<name, count>. */
  names: Map<string, Map<string, number>>;
}

const ctx: ExportContext = {
  inverted: {},
  names: new Map(),
};

function writePrim(lines: string[], obj: Object3D, indent: string): void {
  const parentPath = indent; // we only use indent depth as a parent key
  const name = pickSiblingName(obj, parentPath);
  const isHinge = obj instanceof Group && obj.name.startsWith('Hinge_');
  const def = isHinge ? (obj.userData?.hinge as HingeDef | undefined) : undefined;

  if (obj instanceof Mesh) {
    writeMesh(lines, obj, name, indent);
    return;
  }

  lines.push(`${indent}def Xform "${name}"`);
  lines.push(`${indent}{`);
  writeTransform(lines, indent + '    ', localMatrix(obj));
  if (def) writeHingeMetadata(lines, def, indent + '    ');
  for (const child of obj.children) {
    writePrim(lines, child, indent + '    ');
  }
  lines.push(`${indent}}`);
}

function localMatrix(obj: Object3D): Matrix4 {
  const m = new Matrix4();
  m.compose(obj.position, obj.quaternion, obj.scale);
  return m;
}

function writeMesh(lines: string[], mesh: Mesh, name: string, indent: string): void {
  lines.push(`${indent}def Xform "${name}"`);
  lines.push(`${indent}{`);
  writeTransform(lines, indent + '    ', localMatrix(mesh));
  const meshName = `${name}_geom`;
  lines.push(`${indent}    def Mesh "${meshName}"`);
  lines.push(`${indent}    {`);
  writeMeshGeom(lines, mesh, indent + '        ');
  lines.push(`${indent}    }`);
  for (const child of mesh.children) {
    writePrim(lines, child, indent + '    ');
  }
  lines.push(`${indent}}`);
}

function writeMeshGeom(lines: string[], mesh: Mesh, indent: string): void {
  const geo = mesh.geometry as BufferGeometry;
  if (!geo || !geo.attributes.position) {
    lines.push(`${indent}# (no geometry)`);
    return;
  }

  const posArr = geo.attributes.position.array as ArrayLike<number>;
  const points: number[][] = [];
  for (let i = 0; i < posArr.length; i += 3) {
    points.push([posArr[i]!, posArr[i + 1]!, posArr[i + 2]!]);
  }
  const indices: number[] = [];
  if (geo.index) {
    const ia = geo.index.array as ArrayLike<number>;
    for (let i = 0; i < ia.length; i++) indices.push(ia[i]!);
  } else {
    for (let i = 0; i < points.length; i++) indices.push(i);
  }
  const triCount = Math.floor(indices.length / 3);
  if (triCount === 0) {
    lines.push(`${indent}# (empty mesh)`);
    return;
  }

  // Bounding extent.
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (const p of points) {
    for (let k = 0; k < 3; k++) {
      if (p[k]! < min[k]!) min[k] = p[k]!;
      if (p[k]! > max[k]!) max[k] = p[k]!;
    }
  }
  lines.push(`${indent}float3[] extent = [(${fmt3(min as [number, number, number])}), (${fmt3(max as [number, number, number])})]`);

  // Points.
  lines.push(`${indent}point3f[] points = [`);
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const sep = i < points.length - 1 ? ',' : '';
    lines.push(`${indent}    (${fmt3(p as [number, number, number])})${sep}`);
  }
  lines.push(`${indent}]`);

  lines.push(`${indent}int[] faceVertexCounts = [${repeatList('3', triCount)}]`);
  lines.push(`${indent}int[] faceVertexIndices = [`);
  for (let t = 0; t < triCount; t++) {
    const a = indices[t * 3]!;
    const b = indices[t * 3 + 1]!;
    const c = indices[t * 3 + 2]!;
    const sep = t < triCount - 1 ? ',' : '';
    lines.push(`${indent}    ${a}, ${b}, ${c}${sep}`);
  }
  lines.push(`${indent}]`);

  const color = materialDisplayColor(mesh.material);
  lines.push(`${indent}color3f[] primvars:displayColor = [(${fmt3(color)})] (`);
  lines.push(`${indent}    interpolation = "constant"`);
  lines.push(`${indent})`);

  lines.push(`${indent}uniform token subdivisionScheme = "none"`);
}

/* -------------------- transforms ------------------------------------- */

function writeTransform(lines: string[], indent: string, matrix: Matrix4): void {
  const t = new Vector3();
  const q = new Quaternion();
  const s = new Vector3();
  matrix.decompose(t, q, s);
  const isIdentityT = nearZero(t.x) && nearZero(t.y) && nearZero(t.z);
  const isIdentityQ = nearZero(q.x) && nearZero(q.y) && nearZero(q.z) && near(q.w, 1);
  const isIdentityS = near(s.x, 1) && near(s.y, 1) && near(s.z, 1);
  if (isIdentityT && isIdentityQ && isIdentityS) {
    return;
  }
  const ops: string[] = [];
  if (!isIdentityT) {
    lines.push(`${indent}double3 xformOp:translate = (${fmt3([t.x, t.y, t.z])})`);
    ops.push('"xformOp:translate"');
  }
  if (!isIdentityQ) {
    lines.push(`${indent}quatf xformOp:orient = (${q.w}, ${q.x}, ${q.y}, ${q.z})`);
    ops.push('"xformOp:orient"');
  }
  if (!isIdentityS) {
    lines.push(`${indent}double3 xformOp:scale = (${fmt3([s.x, s.y, s.z])})`);
    ops.push('"xformOp:scale"');
  }
  lines.push(`${indent}uniform token[] xformOpOrder = [${ops.join(', ')}]`);
}

/* -------------------- hinges ----------------------------------------- */

function writeHingeMetadata(lines: string[], def: HingeDef, indent: string): void {
  lines.push(`${indent}# Hinge: ${escapeString(def.label)}`);
  lines.push(`${indent}custom string hinge:id = "${escapeString(def.id)}"`);
  lines.push(`${indent}custom string hinge:label = "${escapeString(def.label)}"`);
  lines.push(`${indent}custom string hinge:category = "${def.category}"`);
  lines.push(`${indent}custom token hinge:axis = "${def.axis}"`);
  lines.push(`${indent}custom float hinge:minDeg = ${def.minDeg}`);
  lines.push(`${indent}custom float hinge:maxDeg = ${def.maxDeg}`);
  lines.push(`${indent}custom int hinge:openSignFromMin = ${def.openSignFromMin}`);
  const inv = ctx.inverted[def.id] ?? false;
  lines.push(`${indent}custom bool hinge:inverted = ${inv ? 1 : 0}`);
}

/* -------------------- helpers ---------------------------------------- */

function pickSiblingName(obj: Object3D, parentPath: string): string {
  // Drop the Hinge_ prefix from pivot nodes — in USDA the enclosing
  // xform name should read as a DOM element (the hinge), not as an
  // "implementation detail". The hinge metadata is already attached as
  // custom attributes, so callers can still find it via `hinge:id`.
  let raw = obj.name || obj.type || 'Prim';
  if (obj instanceof Group && raw.startsWith('Hinge_')) {
    const def = obj.userData?.hinge as HingeDef | undefined;
    raw = `Hinge_${def?.category ?? 'joint'}_${def?.id ?? 'anon'}`;
  }
  const base = sanitisePrimName(raw) || 'Prim';
  const siblingNames = ctx.names.get(parentPath) ?? new Map<string, number>();
  const count = siblingNames.get(base) ?? 0;
  siblingNames.set(base, count + 1);
  ctx.names.set(parentPath, siblingNames);
  if (count === 0) return base;
  return `${base}_${count.toString().padStart(3, '0')}`;
}

const NAME_RE = /[^A-Za-z0-9_]/g;
function sanitisePrimName(s: string): string {
  if (!s) return '';
  let out = s.replace(NAME_RE, '_');
  if (/^[0-9]/.test(out)) out = '_' + out;
  return out;
}

function escapeString(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function fmt(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return Number(n.toFixed(6)).toString();
}

function fmt3(p: [number, number, number]): string {
  return `${fmt(p[0])}, ${fmt(p[1])}, ${fmt(p[2])}`;
}

function repeatList(token: string, count: number): string {
  if (count <= 0) return '';
  if (count <= 16) return new Array(count).fill(token).join(', ');
  const chunks: string[] = [];
  for (let i = 0; i < count; i += 16) {
    const len = Math.min(16, count - i);
    chunks.push(new Array(len).fill(token).join(', '));
  }
  return chunks.join(',\n        ');
}

function nearZero(n: number): boolean {
  return Math.abs(n) < 1e-9;
}
function near(n: number, target: number): boolean {
  return Math.abs(n - target) < 1e-9;
}

function materialDisplayColor(material: Material | Material[]): [number, number, number] {
  const mat = Array.isArray(material) ? material[0]! : material;
  if (
    mat instanceof MeshStandardMaterial ||
    mat instanceof MeshPhysicalMaterial ||
    mat instanceof MeshBasicMaterial
  ) {
    const c = mat.color as Color;
    return [c.r, c.g, c.b];
  }
  return [0.7, 0.7, 0.7];
}
